import React, { useEffect } from 'react'
import { InsightType, MetricTimeUnit } from 'enums'
import { createMetric } from 'utils/runSearchUtil'
import { useAppDispatch } from 'app/hooks'
import { getInsightContentData } from 'features/insightContent/asyncThunks'
import { getMetricSeries } from 'utils/measureTypeUtil'
import { InsightData } from 'schemas/action/InsightDataSchema'
import { CountTypeStrings } from 'enums/CountType'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { getMeasureTypeByCountType } from 'components/insightContent/utils/utils'
import { InsightContentSettingType } from 'components/insightContent/types'
import { getSparklineMetricTimeUnit } from 'utils/sparklineMetricTimeUnit'
import { generateSentimentInsightStrategy } from 'utils/insightModal/strategy/searchFilterComposing/'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import useRefreshTimestamp from 'hooks/useRefreshTimestamp'

const resolveInsightsForDetailSearchFilter = (viewOption: InsightViewOptionStrings) => {
  const insights: InsightData[] = []

  switch (viewOption) {
    case InsightViewOption.ATTRIBUTES:
      insights.push({ insightType: InsightType.LIKES } as InsightData)
      insights.push({ insightType: InsightType.DISLIKES } as InsightData)
      return insights

    case InsightViewOption.BEHAVIORS:
      insights.push({ insightType: InsightType.POSITIVE_BEHAVIORS } as InsightData)
      insights.push({ insightType: InsightType.NEGATIVE_BEHAVIORS } as InsightData)
      return insights

    case InsightViewOption.EMOTIONS:
      insights.push({ insightType: InsightType.POSITIVE_EMOTIONS } as InsightData)
      insights.push({ insightType: InsightType.NEGATIVE_EMOTIONS } as InsightData)
      return insights

    default:
      return insights
  }
}

const createSearch = (detailSetting: InsightContentSettingType, countType: CountTypeStrings) => {
  const { filter, precision, topicId, viewOption } = detailSetting
  const strategy = generateSentimentInsightStrategy()
  const insights = resolveInsightsForDetailSearchFilter(viewOption)
  const alteredFilter = strategy.composeDetailSearchFilter(filter, insights)

  const timeUnits = [MetricTimeUnit.ALL, getSparklineMetricTimeUnit(filter?.publishedDate)]
  const option = {
    forceNotScale: false
  }
  const measure = getMeasureTypeByCountType(countType)

  return createMetric(
    -1,
    '',
    precision!,
    [topicId!],
    alteredFilter,
    getMetricSeries(measure),
    timeUnits,
    option
  )
}

const RunSearchEffect = ({
  detailSetting,
  countType,
  children
}: {
  detailSetting: InsightContentSettingType
  countType: CountTypeStrings
  children: React.ReactNode
}) => {
  const dispatch = useAppDispatch()
  const refreshTimestamp = useRefreshTimestamp()

  useEffect(() => {
    const searches = createSearch(detailSetting, countType)

    dispatch(
      getInsightContentData({
        insightContentType: InsightContentType.MEASURE_WITH_INSIGHTS,
        searches: [searches],
        timestamp: refreshTimestamp
      })
    )
  }, [dispatch, detailSetting, countType, refreshTimestamp])

  return <>{children}</>
}

export default RunSearchEffect
